<template>
    <!-- a tap anywhere dismisses it, the card and the X included -->
    <div class="kiosk-qr-overlay" @click="emit('close')">
        <div class="card">
            <button type="button" class="closer" @click.stop="emit('close')">
                <i class="fal fa-times"></i>
            </button>
            <div class="title">{{ translations.kiosk.scanToRead }}</div>
            <img v-if="imageDataUrl" class="code" :src="imageDataUrl" alt="" />
            <div v-else class="code"></div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { onBeforeUnmount, onMounted, ref, watch } from "vue";

    import { Factory } from "@/data/dao/Factory";
    import { QrCodeDao } from "@/data/dao/service/QrCodeDao";
    import { logError } from "@/shared/logging/errors";

    import { appEvents } from "@/app/events/appEvents";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { translations } from "@/shared/localization/translations";

    const props = defineProps<{
        url: string;
    }>();

    const emit = defineEmits<{
        close: [];
    }>();

    const imageDataUrl = ref<string | null>(null);

    // the question whether the order goes on is asked on top of everything, the code must not cover it
    const onInactivityPopupShown = () => emit("close");

    onBeforeUnmount(() => appEvents.kiosk.inactivityPopupShown.remove(onInactivityPopupShown));

    // the next customer must not find the code of the previous one on the screen
    const kiosk = useKioskStore();
    watch(
        () => kiosk.isScreenSaverVisible,
        (isVisible) => {
            if (isVisible) emit("close");
        }
    );

    onMounted(async () => {
        appEvents.kiosk.inactivityPopupShown.add(onInactivityPopupShown);

        try {
            const qrCode = await Factory.getInstance<QrCodeDao>(QrCodeDao).getQrCode({ url: props.url });
            imageDataUrl.value = qrCode.imageDataUrl;
        } catch (ex) {
            // nothing to scan, so there is nothing left to show
            logError(ex);
            emit("close");
        }
    });
</script>

<style lang="scss" scoped>
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    // kiosk only, so the sizes are written with the scale instead of under .kiosk-mode. above the
    // popovers and their dim (410 / 430), below the screen saver (9999)
    .kiosk-qr-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 500;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: rgba(0, 0, 0, 0.7);
        cursor: pointer;

        > .card {
            position: relative;
            box-sizing: border-box;
            padding: (28px * variables.$kioskScale) (32px * variables.$kioskScale);
            border-radius: 15px * variables.$kioskScale;
            background-color: #fff;
            text-align: center;

            > .closer {
                position: absolute;
                top: 0;
                right: 0;
                width: 56px * variables.$kioskScale;
                height: 56px * variables.$kioskScale;
                padding: 0;
                border: none;
                background-color: transparent;
                cursor: pointer;

                > .fal {
                    font-size: 18px * variables.$kioskScale;
                    line-height: 56px * variables.$kioskScale;
                }
            }

            > .title {
                // the closer sits in the top corner of the card, the title stays clear of it
                padding: 0 (40px * variables.$kioskScale);
                font-size: 20px * variables.$kioskScale;
                line-height: 28px * variables.$kioskScale;
                font-weight: 400;
            }

            > .code {
                display: block;
                width: 240px * variables.$kioskScale;
                height: 240px * variables.$kioskScale;
                margin-top: 16px * variables.$kioskScale;
            }
        }
    }
</style>
